
import { useMemo } from "react";
import { OpeningHour } from "@/hooks/useOpeningHours";

export interface OpeningStatus {
  isOpen: boolean;
  statusText: string;
  statusWord?: string; // The main status word (Open/Closed)
  remainingText?: string; // The rest of the status text
  nextChangeTime?: string;
  nextChangeDay?: string;
}

export const useProviderOpeningStatus = (openingHours?: OpeningHour[]): OpeningStatus => {
  return useMemo(() => {
    if (!openingHours || openingHours.length === 0) {
      return {
        isOpen: false,
        statusText: "Hours not available"
      };
    }

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM format

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Find today's hours
    const todayHours = openingHours.find(hour => hour.day_of_week === currentDay);

    if (!todayHours || todayHours.is_closed) {
      // Find next opening day
      let nextOpenDay = null;
      let nextOpenTime = null;
      
      for (let i = 1; i <= 7; i++) {
        const checkDay = (currentDay + i) % 7;
        const dayHours = openingHours.find(hour => hour.day_of_week === checkDay);
        
        if (dayHours && !dayHours.is_closed) {
          nextOpenDay = dayNames[checkDay];
          nextOpenTime = formatTime(dayHours.open_time);
          break;
        }
      }

      if (nextOpenDay && nextOpenTime) {
        return {
          isOpen: false,
          statusText: `Closed - opens on ${nextOpenDay} at ${nextOpenTime}`,
          statusWord: "Closed",
          remainingText: ` - opens on ${nextOpenDay} at ${nextOpenTime}`,
          nextChangeDay: nextOpenDay,
          nextChangeTime: nextOpenTime
        };
      } else {
        return {
          isOpen: false,
          statusText: "Closed",
          statusWord: "Closed"
        };
      }
    }

    // Check if currently open
    const openTime = todayHours.open_time;
    const closeTime = todayHours.close_time;

    if (isTimeInRange(currentTime, openTime, closeTime)) {
      return {
        isOpen: true,
        statusText: `Open - closes at ${formatTime(closeTime)}`,
        statusWord: "Open",
        remainingText: ` - closes at ${formatTime(closeTime)}`,
        nextChangeTime: formatTime(closeTime)
      };
    } else {
      // Closed today but will open later today
      if (currentTime < openTime) {
        return {
          isOpen: false,
          statusText: `Closed - opens today at ${formatTime(openTime)}`,
          statusWord: "Closed",
          remainingText: ` - opens today at ${formatTime(openTime)}`,
          nextChangeTime: formatTime(openTime)
        };
      } else {
        // Already closed for today, find next opening
        let nextOpenDay = null;
        let nextOpenTime = null;
        
        for (let i = 1; i <= 7; i++) {
          const checkDay = (currentDay + i) % 7;
          const dayHours = openingHours.find(hour => hour.day_of_week === checkDay);
          
          if (dayHours && !dayHours.is_closed) {
            nextOpenDay = dayNames[checkDay];
            nextOpenTime = formatTime(dayHours.open_time);
            break;
          }
        }

        if (nextOpenDay && nextOpenTime) {
          return {
            isOpen: false,
            statusText: `Closed - opens on ${nextOpenDay} at ${nextOpenTime}`,
            statusWord: "Closed",
            remainingText: ` - opens on ${nextOpenDay} at ${nextOpenTime}`,
            nextChangeDay: nextOpenDay,
            nextChangeTime: nextOpenTime
          };
        } else {
          return {
            isOpen: false,
            statusText: "Closed",
            statusWord: "Closed"
          };
        }
      }
    }
  }, [openingHours]);
};

// Helper function to check if current time is within opening hours
const isTimeInRange = (currentTime: string, openTime: string, closeTime: string): boolean => {
  const current = timeToMinutes(currentTime);
  const open = timeToMinutes(openTime);
  const close = timeToMinutes(closeTime);

  // Handle cases where closing time is past midnight
  if (close < open) {
    return current >= open || current <= close;
  }
  
  return current >= open && current <= close;
};

// Convert time string (HH:MM) to minutes since midnight
const timeToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

// Format time from 24-hour to 12-hour format
const formatTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};
